import { Plus, X } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { confirmModal } from '@/components/ui/modal'
import { apiErrorMessage } from '@/i18n'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../shared/format'
import { useAddDashNote, useDashNotes, useDeleteDashNote } from '../api'
import type { DashNote, NoteColor, WidgetBodyProps } from '../types'
import { WidgetItems, WidgetState } from './WidgetItems'

const DOT: Record<NoteColor, string> = { primary: 'bg-primary', warning: 'bg-warning', success: 'bg-success', violet: 'bg-violet' }

export function NotesWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const q = useDashNotes()
  const add = useAddDashNote()
  const remove = useDeleteDashNote()
  const [text, setText] = useState('')

  const fail = (err: unknown) => toast.error(apiErrorMessage(err))

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!text.trim()) return
    add.mutate(text, { onSuccess: () => setText(''), onError: fail })
  }

  async function onDelete(note: DashNote) {
    const ok = await confirmModal({
      title: t('dashboard.widgets.notes.deleteTitle'),
      description: t('dashboard.widgets.notes.deleteBody', { text: note.text }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (ok) remove.mutate(note.id, { onSuccess: () => toast.success(t('common.deleted')), onError: fail })
  }

  const items = (q.data ?? []).map((note) => ({
    id: note.id,
    title: note.text,
    leading: <span className={cn('size-2 shrink-0 rounded-full', DOT[note.color])} aria-hidden />,
    meta: fmtDate(note.created_at),
    trailing: (
      <button
        type="button"
        aria-label={t('dashboard.widgets.notes.delete', { text: note.text })}
        onClick={() => void onDelete(note)}
        className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="size-3.5" />
      </button>
    ),
  }))

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="min-h-0 flex-1">
        <WidgetState query={q} empty={items.length === 0} emptyText={t('dashboard.widgets.notes.empty')}>
          <WidgetItems items={items} view={config.view} density={config.density} />
        </WidgetState>
      </div>
      <form onSubmit={onSubmit} className="sticky bottom-0 flex gap-1.5 bg-card pt-1">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('dashboard.widgets.notes.placeholder')}
          aria-label={t('dashboard.widgets.notes.placeholder')}
          maxLength={120}
          className="h-8 text-xs"
        />
        <button
          type="submit"
          disabled={!text.trim() || add.isPending}
          aria-label={t('dashboard.widgets.notes.add')}
          className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          <Plus className="size-4" />
        </button>
      </form>
    </div>
  )
}
